import { get } from 'lodash';
import { SOURCE_CONTEXT_NAMES, WIDGET_LOGICAL_NAMES } from '@config/Consts';
import BaseService from '@core/common/BaseService';
import { ReportingClient, RESOURCES as REPORTING_RESOURCES } from '@core/clients/ReportingClient';
import { MsgHistoryClient, RESOURCES as MSG_HISTORY_RESOURCES, AGENT_STATUS_RESOURCES } from '@core/clients/MsgHistoryClient';
import { AgentManagerClient, RESOURCES as AGENT_MANAGER_RESOURCES } from '@core/clients/AgentManagerClient';
import { Subscription } from '@core/common/Subscription';
import shiftStatusTransformer from './shiftStatusTransformer';

const cancellationTokens = {};

function getEligibleResources(useNewManagerApi, fetchResponseMetrics) {
  const resources = Object.assign({}, {
    MESSAGING_CSAT_DISTRIBUTIONS: REPORTING_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS,
    MESSAGING_CURRENT_QUEUE_HEALTH: REPORTING_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH,
  });
  if (fetchResponseMetrics || useNewManagerApi) {
    resources.METRICS = AGENT_MANAGER_RESOURCES.METRICS;
  }
  if (!useNewManagerApi) {
    resources.MESSAGING_CONVERSATIONS = REPORTING_RESOURCES.MESSAGING_CONVERSATIONS;
    resources.AGENT_STATUS_ASSIGNED = AGENT_STATUS_RESOURCES.AGENT_STATUS_ASSIGNED;
    resources.AGENT_STATUS_LOAD = AGENT_STATUS_RESOURCES.AGENT_STATUS_LOAD;
  }
  return resources;
}

function createShiftStatusClients(params) {
  const contextName = SOURCE_CONTEXT_NAMES[WIDGET_LOGICAL_NAMES.SHIFT_STATUS];
  const messagingCsatDistributionClient = new ReportingClient({
    resource: REPORTING_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS,
    contextName,
  });
  messagingCsatDistributionClient.useRequestInterceptor(params.interceptor);
  const messagingQueueHealthDistributionClient = new ReportingClient({
    resource: REPORTING_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH,
    contextName,
  });

  const RESOURCES_TO_CLIENT_MAP = {
    [REPORTING_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS]: messagingCsatDistributionClient,
    [REPORTING_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH]: messagingQueueHealthDistributionClient,
  };

  if (params.useNewManagerApi || params.fetchResponseMetrics) {
    const metricsClient = new AgentManagerClient({
      resource: AGENT_MANAGER_RESOURCES.METRICS,
      contextName,
    });
    metricsClient.useRequestInterceptor(params.interceptor);
    RESOURCES_TO_CLIENT_MAP[AGENT_MANAGER_RESOURCES.METRICS] = metricsClient;
  }
  if (!params.useNewManagerApi) {
    const messagingConversationClient = new ReportingClient({
      resource: REPORTING_RESOURCES.MESSAGING_CONVERSATIONS,
      contextName,
    });
    messagingConversationClient.useRequestInterceptor(params.interceptor);
    const agentStatusAssignedClient = new MsgHistoryClient({
      resource: MSG_HISTORY_RESOURCES.AGENT_STATUS,
      contextName,
    });
    const agentStatusLoadClient = new MsgHistoryClient({
      resource: MSG_HISTORY_RESOURCES.AGENT_STATUS,
      contextName,
    });
    RESOURCES_TO_CLIENT_MAP[REPORTING_RESOURCES.MESSAGING_CONVERSATIONS] =
      messagingConversationClient;
    RESOURCES_TO_CLIENT_MAP[AGENT_STATUS_RESOURCES.AGENT_STATUS_ASSIGNED] =
      agentStatusAssignedClient;
    RESOURCES_TO_CLIENT_MAP[REPORTING_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH] =
      messagingQueueHealthDistributionClient;
    RESOURCES_TO_CLIENT_MAP[AGENT_STATUS_RESOURCES.AGENT_STATUS_LOAD] =
      agentStatusLoadClient;
  }

  const getClient = resourceName => RESOURCES_TO_CLIENT_MAP[resourceName];

  return {
    getClient,
  };
}

function stopListenToShiftStatusClientEvents(resourceName) {
  const client = this.shiftStatusClients.getClient(resourceName);
  client.clearAllListeners(client.constructor.EVENTS.onPollingSuccess);
  client.clearAllListeners(client.constructor.EVENTS.onPollingError);
}

async function enrichShiftStatusData(resourceName, shiftStatusResponse) {
  const enrichedShiftStatusResponse = shiftStatusTransformer.transformResponse(resourceName, {
    shiftStatusResponse,
  });
  const resourceSubscription = this.subscriptions.find(
    subscriptionRecord => subscriptionRecord.name === resourceName);
  resourceSubscription.subscription.emit(Subscription.EVENTS.onSubscriptionUpdateNotification,
    { name: resourceName, data: enrichedShiftStatusResponse });
}

function onShiftStatusSubscriptionError(resourceName, error) {
  const resourceSubscription = this.subscriptions.find(
    subscriptionRecord => subscriptionRecord.name === resourceName);
  resourceSubscription.subscription.emit(Subscription.EVENTS.onSubscriptionError, error);
}

function listenToShiftStatusClientEvents(settings, resourceName) {
  stopListenToShiftStatusClientEvents.call(this, resourceName);
  const client = this.shiftStatusClients.getClient(resourceName);
  const clientEvents = client.constructor.EVENTS;
  client.on(
    clientEvents.onPollingSuccess,
    enrichShiftStatusData.bind(this, resourceName),
    'ActivitySummaryServicePollingSuccess');
  client.on(
    clientEvents.onPollingError, onShiftStatusSubscriptionError.bind(this, resourceName),
    'ActivitySummaryServicePollingError');
}

function shiftStatusSubscriptionStartMethod(settings) {
  const request = shiftStatusTransformer.transformRequest(settings.name, settings.settings);

  listenToShiftStatusClientEvents.call(this, settings.settings, settings.name);
  cancellationTokens[settings.name] = this.shiftStatusClients.getClient(settings.name)
    .subscribe(request);
}

function shiftStatusSubscriptionStopMethod(settings) {
  const token = get(cancellationTokens, settings.name);
  if (token) {
    stopListenToShiftStatusClientEvents.call(this, settings.name);
    this.shiftStatusClients.getClient(settings.name).unsubscribe(token);
  }
}

/**
 * Shift Status service - retrieves the shiftStatus data for Shift Status widget.
 * Requests data 3 different APIs:
 * Messaging Conversation
 * Messaging CSAT Distribution
 * Closed Conversations
 */
class ShiftStatusService extends BaseService {
  constructor() {
    super({ name: 'ShiftStatusService' });
  }

  async getShiftStatusSubscriptions(params) {
    if (!this.subscriptions) {
      this.shiftStatusClients = createShiftStatusClients(params);
      this.subscriptions = [];
      const resources = getEligibleResources(params.useNewManagerApi, params.fetchResponseMetrics);
      Object.keys(resources).forEach((key) => {
        const resourceName = resources[key];
        const resourceSubscription = new Subscription({
          name: `${resourceName}`,
          resourceName,
          handlers: {
            start: shiftStatusSubscriptionStartMethod.bind(this),
            stop: shiftStatusSubscriptionStopMethod.bind(this),
          },
        });
        this.subscriptions.push({ name: resourceName, subscription: resourceSubscription });
      });
      return { subscriptions: this.subscriptions };
    }
    return { subscriptions: this.subscriptions };
  }
}

const shiftStatusService = new ShiftStatusService();

export default shiftStatusService;
